import type { SmartFolderRovoConnectorProvider } from './SmartFolderRovoConnectorProviders.ts';

type SmartFolderRecoverableSyncState = 'emptyFolder' | 'waitingForTWG';
type SmartFolderRecoverableSyncRetryResult =
	| 'success'
	| 'stillEmpty'
	| 'stillWaitingForTWG'
	| 'failed';
export type SmartFolderRecoverableSyncSourceSurface =
	| 'paste-link'
	| 'suggestion-picker'
	| 'folder-view-auto-sync'
	| 'manual-retry';
export type SmartFolderRecoverableSyncProvider = SmartFolderRovoConnectorProvider | 'unknown';

type SmartFolderSyncMutationError = {
	message?: string | null;
	extensions?: {
		errorType?: string | null;
	} | null;
};

const waitingForTWGStartedAtByFolderId = new Map<string, number>();

const RECOVERABLE_SYNC_OBSERVATION_WINDOW_MS = 15 * 60 * 1000;

export const getSyncErrorType = (
	errors: ReadonlyArray<SmartFolderSyncMutationError> | null | undefined,
): string | undefined =>
	errors?.find((error) => typeof error.extensions?.errorType === 'string')?.extensions?.errorType ??
	undefined;

const getRecoverableSyncState = (
	syncErrorType: string | undefined,
): SmartFolderRecoverableSyncState | undefined => {
	switch (syncErrorType) {
		case 'SYNC_EXTERNAL_FOLDER_EMPTY':
		case 'SYNC_SOURCE_FOLDER_EMPTY':
			return 'emptyFolder';
		case 'SYNC_WAITING_FOR_TWG':
		case 'SYNC_INDEXING_IN_PROGRESS':
			return 'waitingForTWG';
		default:
			return undefined;
	}
};

const getRecoverableSyncRetryResult = ({
	success,
	syncErrorType,
}: {
	success: boolean;
	syncErrorType?: string;
}): SmartFolderRecoverableSyncRetryResult => {
	if (success) {
		return 'success';
	}

	switch (getRecoverableSyncState(syncErrorType)) {
		case 'emptyFolder':
			return 'stillEmpty';
		case 'waitingForTWG':
			return 'stillWaitingForTWG';
		default:
			return 'failed';
	}
};

const getWaitingForTWGDurationAttributes = ({
	folderId,
	now,
	retryResult,
	syncErrorType,
}: {
	folderId: string;
	now: number;
	retryResult: SmartFolderRecoverableSyncRetryResult;
	syncErrorType?: string;
}): {
	timeFromFirstWaitingForTWGToSuccessfulSyncMs?: number;
	noSuccessfulRetryWithinObservationWindow?: boolean;
} => {
	if (getRecoverableSyncState(syncErrorType) === 'waitingForTWG') {
		if (!waitingForTWGStartedAtByFolderId.has(folderId)) {
			waitingForTWGStartedAtByFolderId.set(folderId, now);
		}
	}

	const waitingForTWGStartedAt = waitingForTWGStartedAtByFolderId.get(folderId);
	if (waitingForTWGStartedAt === undefined) {
		return {};
	}

	if (retryResult === 'success') {
		waitingForTWGStartedAtByFolderId.delete(folderId);
		return {
			timeFromFirstWaitingForTWGToSuccessfulSyncMs: now - waitingForTWGStartedAt,
		};
	}

	return now - waitingForTWGStartedAt >= RECOVERABLE_SYNC_OBSERVATION_WINDOW_MS
		? { noSuccessfulRetryWithinObservationWindow: true }
		: {};
};

export const getRecoverableSyncAnalyticsAttributes = ({
	containerApp,
	durationMs,
	folderId,
	isRetry,
	now,
	provider,
	retryCount,
	sourceSurface,
	success,
	syncErrorType,
}: {
	containerApp?: string;
	durationMs?: number;
	folderId: string;
	isRetry: boolean;
	now: number;
	provider: SmartFolderRecoverableSyncProvider;
	retryCount?: number;
	sourceSurface: SmartFolderRecoverableSyncSourceSurface;
	success: boolean;
	syncErrorType?: string;
}): {
	timeFromFirstWaitingForTWGToSuccessfulSyncMs?: number;
	noSuccessfulRetryWithinObservationWindow?: boolean;
	recoverableState?: SmartFolderRecoverableSyncState | undefined;
	syncErrorType?: string | undefined;
	retryCount?: number | undefined;
	durationMs?: number | undefined;
	folderId: string;
	provider: SmartFolderRecoverableSyncProvider;
	containerApp: string;
	sourceSurface: SmartFolderRecoverableSyncSourceSurface;
	isRetry: boolean;
	retryResult: SmartFolderRecoverableSyncRetryResult;
	observationWindowMs: number;
} => {
	const retryResult = getRecoverableSyncRetryResult({ success, syncErrorType });
	const recoverableState = getRecoverableSyncState(syncErrorType);

	return {
		folderId,
		provider,
		containerApp: containerApp ?? 'unknown',
		sourceSurface,
		isRetry,
		retryResult,
		observationWindowMs: RECOVERABLE_SYNC_OBSERVATION_WINDOW_MS,
		...(durationMs !== undefined ? { durationMs } : {}),
		...(retryCount !== undefined ? { retryCount } : {}),
		...(syncErrorType !== undefined ? { syncErrorType } : {}),
		...(recoverableState !== undefined ? { recoverableState } : {}),
		...getWaitingForTWGDurationAttributes({
			folderId,
			now,
			retryResult,
			syncErrorType,
		}),
	};
};
